// src/pages/NewRequest.js
// Full-page "New Request" form used for both file requests and credential requests.
// Replaces the former NewRequestModal dialogs so every "New" action is a proper page.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

const CONFIG = {
  file: {
    requestType: 'file',
    eyebrow: 'Files',
    title: 'New File Request',
    subtitle: 'Request files from an external party — they receive a secure single-use upload link',
    defaultTitle: 'File Request',
    titlePlaceholder: 'e.g. Contract Documents, ID Verification',
    recipientHint: "They'll receive a secure single-use upload link",
    listPath: '/filerequests',
    listLabel: 'View file requests',
    sendLabel: '📥 Send File Request',
    successNotified: 'The recipient has been notified by email with a secure upload link.',
    security: [
      ['📧', 'The recipient verifies their identity with a one-time email code'],
      ['📤', 'Files are uploaded directly to secure storage'],
      ['🔗', 'The upload link can only be used once'],
      ['⏱', 'The link auto-expires on the date you choose'],
      ['📋', 'Every access attempt is logged in a full audit trail'],
    ],
    expiryNote: 'The link stops working once files have been submitted, regardless of date',
  },
  credential: {
    requestType: 'credential',
    eyebrow: 'Credentials',
    title: 'New Credential Request',
    subtitle: 'Request passwords, API keys and sensitive text from an external party — single-use secure link',
    defaultTitle: 'Credential Request',
    titlePlaceholder: 'e.g. API Key, VPN Password',
    recipientHint: "They'll receive a secure single-use submission link",
    listPath: '/credentialrequests',
    listLabel: 'View credential requests',
    sendLabel: '🔐 Send Credential Request',
    successNotified: 'The recipient has been notified by email with a secure submission link.',
    security: [
      ['📧', 'The recipient verifies their identity with a one-time email code'],
      ['🔐', 'Submitted credentials are AES-256 encrypted at rest'],
      ['🔗', 'The submission link can only be used once'],
      ['⏱', 'The link auto-expires on the date you choose'],
      ['📋', 'Every access attempt is logged in a full audit trail'],
    ],
    expiryNote: 'The link stops working once credentials have been submitted, regardless of date',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewRequest({ type = 'file' }) {
  const cfg = CONFIG[type] || CONFIG.file;
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [expiryDays, setExpiryDays] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    const email = recipientEmail.trim();
    if (!email) return setError('Recipient email required');
    if (!EMAIL_RE.test(email)) return setError('Please enter a valid email address');
    setError('');
    setSubmitting(true);
    try {
      const res = await apiFetch('requests', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim() || cfg.defaultTitle,
          message: message.trim(),
          recipientEmail: email,
          expiryDays,
          requestType: cfg.requestType,
        }),
      });
      setResult({ ...res, recipientEmail: email });
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setMessage(''); setRecipientEmail(''); setExpiryDays(2);
    setError(''); setResult(null);
  };

  // ── Confirmation screen ──
  if (result) {
    const expiry = result.expiresAt
      ? new Date(result.expiresAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : '';
    return (
      <div>
        <div className="page-eyebrow">{cfg.eyebrow}</div>
        <h1 className="page-title">Request Created</h1>
        <p className="page-subtitle">{cfg.title}</p>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '56px', marginBottom: '14px' }}>{result.emailFailed ? '⚠️' : '✅'}</div>
            <h2 style={{ color: 'var(--g900)', fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>
              {result.emailFailed ? 'Request created — email not sent' : 'Request sent successfully'}
            </h2>
            {result.emailFailed ? (
              <div className="alert alert-error" style={{ textAlign: 'left', maxWidth: '520px', margin: '0 auto 24px' }}>
                The request was created but the email to <strong>{result.recipientEmail}</strong> could not be sent.
                Please contact IT Support (it.support@sufc.co.uk).
              </div>
            ) : (
              <p style={{ color: 'var(--g500)', fontSize: '14px', marginBottom: '24px' }}>
                {cfg.successNotified}
                {expiry && <> Link expires <strong>{expiry}</strong>.</>}
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate(cfg.listPath)}>{cfg.listLabel}</button>
              <button className="btn btn-primary" onClick={resetForm}>Send another</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div>
      <div className="page-eyebrow">{cfg.eyebrow}</div>
      <h1 className="page-title">{cfg.title}</h1>
      <p className="page-subtitle">{cfg.subtitle}</p>

      {error && <div className="alert alert-error">⚠️ {error}</div>}

      <div className="transfer-form-grid">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Request details</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Title <span style={{ color: 'var(--g400)', fontWeight: 400 }}>(optional)</span></label>
                <input className="form-input" placeholder={cfg.titlePlaceholder}
                  value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Recipient email *</label>
                <input className="form-input" type="email" placeholder="external@example.com"
                  value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} />
                <span className="form-hint">{cfg.recipientHint}</span>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Message <span style={{ color: 'var(--g400)', fontWeight: 400 }}>(optional)</span></label>
                <textarea className="form-textarea" rows={6} maxLength={500}
                  placeholder="Instructions or context for the recipient…"
                  value={message} onChange={e => setMessage(e.target.value)}
                  style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                <span className="form-hint">Shown in the email, so the recipient knows it's genuinely from you. Please don't include links. {message.length}/500</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="card" style={{ borderLeft: '3px solid var(--red)' }}>
            <div className="card-body" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--red)', marginBottom: '10px' }}>🔒 Security</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {cfg.security.map(([icon, text]) => (
                  <div key={text} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: '12px', color: 'var(--g600)', lineHeight: '1.4' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Expiry</span></div>
            <div className="card-body">
              <p style={{ fontSize: '12px', color: 'var(--g500)', marginBottom: '10px' }}>How long should this link be active?</p>
              <div className="expiry-options">
                {[1, 2, 3, 4, 5].map(d => (
                  <button key={d} className={`expiry-option${expiryDays === d ? ' selected' : ''}`} onClick={() => setExpiryDays(d)}>
                    <span className="days">{d}</span>
                    <span className="label">{d === 1 ? 'day' : 'days'}</span>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '10px' }}>{cfg.expiryNote}</p>
            </div>
          </div>

          <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
            onClick={handleSubmit} disabled={submitting || !recipientEmail.trim()}>
            {submitting ? 'Sending…' : cfg.sendLabel}
          </button>
          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => navigate(cfg.listPath)} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
